/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cmiller
 * dotPlotElement.js -- constructors for Dyn.DotPlotBase() and derived objects DotPlotElement, ScatterPlotElement, LineGraphElement, BoxPlotElement.
 */

import { PlotElement} from './plotElement'
import { caseSizes, caseSizesDefault } from "../../../utils/constants";

/**
 * Dyn.DotPlotBase()
 * Base element in for dot plots, scatter plots, line graphs, box plots, and map plots.
 * All of which use one+ circle or custom icon per case. Others may use rectangles or pie slices.
 */
export class DotPlotBase extends PlotElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsShape,
            otherControls: []
        }, Dyn );

        this.wantStackUp = true; // else stack left-to-right
        //this.needsDefaultTitle = true;
        this.numAttributesUsedX = 0;
        this.numAttributesUsedY = 0;  // same as Y1, unless Dual Axis, then Y1+Y2
        this.numAttributesUsedY1 = 0; // if Dual Y-axis, count for yAxisView only
        this.numAttributesUsedY2 = 0; // if Dual Y-axis, count for y2AxisView only
    }

    /**
     * Return the allowable number of case icons show per data case, normally 1.
     * Updates:  this.numAttributesUsedX, ..UsedY, ..UsedY1, ..UsedY2,
     *      which are later used by calcAxis() to enable or disable attributes on the axes.
     * @returns {number} -- iconsPerCase
     */
    calcNumIconsPerCase( ioWhatChanged, subViewRef ) {
        // override default to handle multiple attributes on any one axis,
        //  or Y1+Y2 (Dual) axes, possibly with multiple Y1 numeric attributes.
        return this._calcNumIconsPerCaseWithX_Y1_Y2_Axes( ioWhatChanged, subViewRef );
    }

    /**
     * Return the allowable number of case icons show per data case, normally 1.
     * Here we return 2+ if there are multiple attributes on Y or X, and we can plot them
     * in a dotplot/scatterplot/line plot.
     * @returns {number} -- iconsPerCase
     */
    _calcNumIconsPerCaseWithX_Y1_Y2_Axes( ioWhatChanged, subViewRef ) {
        // initial setting of this.numAttributesUsedX and this.numAttributesUsedY, and
        //  then iconsPerCase which depends on these first 2 settings.
        let Dyn = this.dyn,
            iconsPerCase = this.calcNumIconsAllowOneMultiAttributeAxis( ioWhatChanged, subViewRef );

        // now modify that setting to allow for a Y1+Y2 axis, or only a Y1 axis.
        if( this.canUseY2Axis( subViewRef ) && subViewRef.y2AxisView ) {
            let combinedAttributesY1 = subViewRef.yAxisView.allAttributes,
                combinedAttributesY2 = subViewRef.y2AxisView.allAttributes;
            if( subViewRef.yAxisView.isNumericActiveOrInactive() &&     // Y must be numeric for dual axis plot
                subViewRef.y2AxisView.isNumericActiveOrInactive() &&    // Y2 must be numeric for dual axis plot
                combinedAttributesY2.count() === 1 ) {        // for now limit to one attribute on Y2
                // allow Dual Axis to increase number of icons per case by including Y2 attribute(s)
                this.numAttributesUsedY1 = combinedAttributesY1.count();
                this.numAttributesUsedY2 = 1; // ok to make a Dual Axis with one Y2 attribute
                this.numAttributesUsedY = this.numAttributesUsedY1 + this.numAttributesUsedY2;
                if( this.numAttributesUsedX > 1 ) {
                    this.numAttributesUsedX = 1; // limit X to one attribute for Dual Axis
                }
                Dyn.assert( this.numAttributesUsedY >= 1 );
                //console.log("set icons per case here");
                iconsPerCase = this.numAttributesUsedY;
            } else {
                this.numAttributesUsedY1 = this.numAttributesUsedY;
                this.numAttributesUsedY2 = 0; // disable categorical on Y2 if any
            }
        } else {
            this.numAttributesUsedY1 = this.numAttributesUsedY;
            this.numAttributesUsedY2 = 0; // hidden Y2 axis
        }
        return iconsPerCase;
    }

    /**
     * Record which cases that have missing values for the given plot.
     */
    calcMissingValueCases( numCases, xAxisView, yAxisView, optionalY2AxisView ) {
        let Dyn = this.dyn,
            missingCases = this.plotShaper.dataSet.missingCases;
        const usePreFilterStats = true; // so that filtered-out categories are not marked as missing (they should be marked as filtered only)

        xAxisView.postCalcAxisUpdate(); // update axis type if changed during calcAxis()
        yAxisView.postCalcAxisUpdate();

        // for each case
        for( let caseIndex = 0; caseIndex < numCases; caseIndex++ ) {

            // for each attribute used in x and y
            let isValid = true;
            for( let attrIndex = 0; isValid && (attrIndex < this.numAttributesUsedX); attrIndex++ ) {
                if( !attributeValueIsValid( caseIndex, attrIndex, xAxisView ) )
                    isValid = false;
            }
            for( let attrIndex = 0; isValid && (attrIndex < this.numAttributesUsedY); attrIndex++ ) {
                if( !attributeValueIsValid( caseIndex, attrIndex, yAxisView ) ) // note: includes dual axis y2
                    isValid = false;
            }
            missingCases.setCaseIsMissing( caseIndex, !isValid );
        }

        // exclude case if any attribute has a missing value (e.g. blank categorical value, non-numeric on numeric axis)
        function attributeValueIsValid( caseIndex, attributeIndex, axisView ) {
            Dyn.assert( ! axisView.needsCalcAxis );
            var value = axisView.getCaseDataAsNumber( caseIndex, attributeIndex, usePreFilterStats );
            return isFinite( value );
        }
    }

    /**
     * update the case positional data (shared by all elements) and adornments (specific to this element) */
    calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments ) {
        let Dyn = this.dyn,
            iconStyler = this.plotShaper.iconStyler,
            dataSet = this.plotShaper.dataSet,
            xAxisType = xAxisView.type,
            yAxisType = yAxisView.type,
            xIsSeparated = xAxisView.isCategoricalActiveAxis(),
            yIsSeparated = yAxisView.isCategoricalActiveAxis(),
            sortingAttribute = dataSet.getOrderByAttribute(),
            numCasesInDataSet = this.plotShaper.numCasesInDataSet;
        const hasBubbles = Boolean(this[Dyn.EPlotControl.kControlsBubbles] && this.hasValidBubbleAttribute()),
            bubbleAttribute = hasBubbles ? this.getBubbleAttribute() : null;

        // set case position for null, categorical, or numeric axis on x and y
        ioPositions.forEach( ( p, i ) => {
            p.caseIndex = i % numCasesInDataSet;
            p.xAttrIndex = ((this.numAttributesUsedX < 2) ? 0 : Dyn.mathUtil.floor( i / numCasesInDataSet ));
            p.yAttrIndex = ((this.numAttributesUsedY < 2) ? 0 : Dyn.mathUtil.floor( i / numCasesInDataSet ));
            p.x = xAxisView.getCaseDataAsNumber( p.caseIndex, p.xAttrIndex );
            p.y = yAxisView.getCaseDataAsNumber( p.caseIndex, p.yAttrIndex );
            p.isVisible = dataSet.isCaseNonMissingNonFiltered( p.caseIndex );
            this.calcBinIndex( p, xIsSeparated, yIsSeparated );
            // convert from data coordinates (numeric value, category index, etc) to view coordinates (pixel position)
            p.x = xAxisView.dataToPixel( p.x );
            p.y = yAxisView.dataToPixel( p.y, p.yAttrIndex );
            p.r = hasBubbles ? getBubbleRadius( bubbleAttribute, p.caseIndex ) : iconStyler.radius;
            setPositionIfMissingOrFiltered( p ); // position all invisible cases offscreen
        });
        this.populateCellsWithCases( ioPositions );

        // stack cases in cell, if needed for the particular combination of x and y axis.
        if( xAxisType === 1 && yAxisType === 1 ) {
            // numeric scatterplot
            this.orderScatterPlotCases( ioPositions, xAxisView, yAxisView, sortingAttribute );
        } else if( xAxisType === 1 || yAxisType === 1 ) {
            // stack to left-to-right or bottom-up against numeric axis
            this.stackInPlace( ioPositions, xAxisView, yAxisView, this.wantStackUp, sortingAttribute );
        } else {
            // single stack in each bin, no axes (yet)
            this.stackInBins( ioPositions, xAxisView, yAxisView, this.wantStackUp, sortingAttribute );
        }

        /**
         * set the position of hidden cases to offscreen (to be animated on screen when status changes)
         * @param p {isVisible,x,y,r} -- case icon position parameters
         */
        function setPositionIfMissingOrFiltered( p ) {
            if( !(p.isVisible) ) {
                p.x = iconStyler.missingValuePosition.x;
                p.y = iconStyler.missingValuePosition.y;
                p.r = Dyn.plotUtil.missingCaseSize;
            }
        }
        function getBubbleRadius( attr, caseIndex ) {
            return attr ? iconStyler.getBubbleRadius( attr.cases[ caseIndex ], attr.stats.max )
                : iconStyler.getBubbleRadius( 0, 1 );
        }
    }

    postCalcPositionUpdate() {
        // Reserved for future use or debugging;
        //  e.g. compare stack size determined by calcPosition() with space used by countElement.calcAdornments()
    }

    orderScatterPlotCases( ioPositions, xAxisView, yAxisView, sortingAttribute ) {
        // scatterplot cases are already positioned; all we have to do now is set the sort order for tabindex (keyboard navigation between cases)
        // to do this we re-arrange the caseIndex array within each cell (there is only one cell for a scatterplot)
        // compare to stackAndOrderCasesInCell() which is used for stacked dot plots
        let Dyn = this.dyn,
            useSortingAttribute = sortingAttribute && this[Dyn.EPlotControl.kControlsCaseOrder],
            sortFunction = useSortingAttribute ? sortValue_orderAttribute : sortValue_AttrIndex_X_Y;
        if( Dyn.plotUtil.enableCaseTabOrder ) {
                this.plotShaper.bins.cells.forEach( ( cell ) => {
                    let sortValues = cell.cases.map( sortFunction );
                    Dyn.dataUtil.sortCaseIndices( sortValues, cell.cases );
                } );
        }
        function sortValue_AttrIndex_X_Y( positionIndex, i ) {
            // sort so that cases of multiple attributes sorted together, then sort on X, then Y
            let p = ioPositions[positionIndex];
            return {
                index: positionIndex, // same as case icon index; iconIndex !== caseIndex when multiple attributes
                value: p.yAttrIndex, // primary sort value for this case
                value2: p.xAttrIndex,
                value3: p.x, // sort by x pixel value first, to make left-to-right
                value4: p.y  // sort equal x values by y pixel value, so top-to-bottom (do we want this?)
            }
        }
        function sortValue_orderAttribute( positionIndex, i ) {
            // sort by order attribute, then by x, then by y
            let p = ioPositions[positionIndex];
            return {
                index: positionIndex, // same as case icon index; iconIndex !== caseIndex when multiple attributes
                value: sortingAttribute.cases[p.caseIndex],
                value2: p.x,
                value3: p.y
            }
        }
    }

    /**
     * Stack the icons against a numeric axis. Assumes one axis is numeric (they stay in place)
     * and on the other axis we are free to adjust the position to make them stack (within the bin)
     * @param ioPositions {*} - {x,y} case positions to change
     * @param xAxisView
     * @param yAxisView
     * @param isStackUp {Boolean} true: stack from bottom to top; false: stack from left to right
     */
    stackInPlace( ioPositions, xAxisView, yAxisView, isStackUp, sortingAttribute ) {
        let Dyn = this.dyn;
        Dyn.assert( isStackUp ? (!yAxisView.isNumericAxis()) : (!xAxisView.isNumericAxis()), 'stacking in wrong direction' );

        function createStacksInCell( cell, numStacks ) {
            // for each cell, create 'cellStacks' array, each stack has 0 cases in it to start
            cell.cellStacks = []; // add temporary stack bins (replace old array if any)
            for( var m = 0, n = numStacks; m < n; ++m ) {
                cell.cellStacks.push( 0 );
            }
        }

        function stackAndOrderCasesInCell( cell, ioPositions, info ) {
            let sortValues = [];

            // 1st pass: get the integer position in a stack (0+)
            cell.cases.forEach( ( positionIndex, i ) => {
                Dyn.assertArrayIndex( ioPositions, positionIndex );
                let p = ioPositions[positionIndex], stackPos;
                if( isStackUp ) {
                    stackPos = addCaseToStack( xAxisView, cell.cellStacks, xAxisView.getCaseDataAsNumber( p.caseIndex, p.xAttrIndex ) );
                    p.y = stackPos.heightIndex;
                } else {
                    stackPos = addCaseToStack( yAxisView, cell.cellStacks, yAxisView.getCaseDataAsNumber( p.caseIndex, p.yAttrIndex ) );
                    p.x = stackPos.heightIndex;
                }
                // save the sort order values for this case icon
                if( Dyn.plotUtil.enableCaseTabOrder ) {
                    sortValues.push( {
                        index: positionIndex, // same as case icon index; iconIndex !== caseIndex when multiple attributes
                        value: stackPos.binIndex,  // primary sort is by index of the stack on axis
                        value2: stackPos.heightIndex
                    } ); // secondary sort is by height index in stack
                }
            } );

            // now resort case icons in this cell, to get correct tabindex order
            if( Dyn.plotUtil.enableCaseTabOrder ) {
                Dyn.dataUtil.sortCaseIndices( sortValues, cell.cases );
            }
        }

        function positionCasesInCell( cell, ioPositions, info ) {
            // 2nd pass: now that we have the stack spacing, convert stack position to view coordinates.
            cell.cases.forEach( ( positionIndex ) => {
                Dyn.assertArrayIndex( ioPositions, positionIndex );
                let p = ioPositions[positionIndex];
                if( isStackUp ) {
                    p.y = (cell.bottom() - info.stackBottom - (p.y * info.stackSpacer));
                } else {
                    p.x = (cell.left() + info.stackBottom + (p.x * info.stackSpacer));
                }
                //p.tabIndex = info.nextTabIndex++; // tab in order from 1st cell to last, then 1st in stack to last
            } );
        }

        function addCaseToStack( axisView, cellStacks, dataValue ) {
            // add the given case to the cells' cellStacks, based on its value on the axis, and return the height in that stack (0+)
            var whichBin = Math.round( (dataValue - info.dataStart) / info.dataBinWidth ),
                heightInStack;

            if( whichBin >= 0 && whichBin < cellStacks.length ) {
                heightInStack = cellStacks[whichBin]++;
            } else {
                heightInStack = 0; // don't stack values outside of range
                Dyn.debugging && console.warn( "addCaseToStack: out-of-range value=" + dataValue );
            }
            Dyn.assert( heightInStack >= 0 );
            return { heightIndex: heightInStack, binIndex: whichBin };
        }

        function findMaxNumCasesInStacks( cellStacks ) {
            // for all 'cellStacks' arrays in all cells, find the maximum number of cases in a stack
            var maxCases = 0, m, n;
            for( m = 0, n = cellStacks.length; m < n; ++m ) {
                if( cellStacks[m] > maxCases ) {
                    maxCases = cellStacks[m];
                }
            }
            return maxCases;
        }

        function deleteStackBins( cell ) {
            delete cell.cellStacks;
        }

        var plotCells = this.plotShaper.bins.cells,
            numCells = plotCells.length,
            maxCasesInStacks = 0,
            info = new StackHelper( Dyn, plotCells, this.plotShaper.iconStyler, isStackUp, false /* no wrapping */, this.stackHeight, this.plotShaper.getCellMargin() ),
            i;
        const kNumStacks = 100;

        info.calcStackSpacing(); // get initial spacing (1st pass)
        info.axisWidth = (isStackUp ? xAxisView.bounds.width : yAxisView.bounds.height); // range along axis
        info.numStacks = kNumStacks;
        // info.stackWidth = this.plotShaper.iconStyler.getWidthOrHeight( isStackUp ); // width of invisible stacks
        // info.numStacks = Math.max( 1, Math.ceil( 1 + (info.axisWidth / info.stackWidth) )); // number of invisible stacks in each cell
        // console.log("numStacks:", info.numStacks, " stackWidth:", info.stackWidth );

        // get data range to determine where stacks boundaries fall in data --CDM 2017-11-20
        let stats = (isStackUp ? xAxisView : yAxisView).allAttributes.stats;
        info.dataWidth = (stats.max !== stats.min) ? (stats.max - stats.min) : 1; // range along axis - checking if min, max is not same.
        info.dataStart = stats.min;
        info.dataBinWidth = info.dataWidth / info.numStacks;

        // create temporary stack array
        for( i = 0; i < numCells; i++ ) {
            if( sortingAttribute ) {
                this.sortCasesInCell( plotCells[i], sortingAttribute, ioPositions, !sortingAttribute.isSortAscending );
            }
            createStacksInCell( plotCells[i], info.numStacks + 1 /* create one more to handle roundoff error, ok if unused */ );
        }
        // stack cases and get initial stack height and position
        for( i = 0; i < numCells; i++ ) {
            stackAndOrderCasesInCell( plotCells[i], ioPositions, info, true );
        }
        // calc new stack spacing to avoid too-high stacks
        for( i = 0; i < numCells; i++ ) {
            maxCasesInStacks = Math.max( maxCasesInStacks, findMaxNumCasesInStacks( plotCells[i].cellStacks ) );
        }
        info.calcStackSpacing( maxCasesInStacks );// get final spacing (2nd pass)

        // move cases into final position
        for( i = 0; i < numCells; i++ ) {
            positionCasesInCell( plotCells[i], ioPositions, info );
        }
        // remove temporary stack array
        for( i = 0; i < numCells; i++ ) {
            deleteStackBins( plotCells[i] );
        }
    }

    /**
     * Stack the case icons within each bin.  Assumes that each plot cell knows what case icons it has.
     * @param ioPositions {*} - {x,y} case positions to change
     * @param isStackUp {Boolean} true: stack from bottom to top; false: stack from left to right
     */
    stackInBins( ioPositions, xAxisView, yAxisView, isStackUp, sortingAttribute ) {
        var Dyn = this.dyn,
            plotCells = this.plotShaper.bins.cells,
            numCells = plotCells.length,
            info = new StackHelper( Dyn, plotCells, this.plotShaper.iconStyler, isStackUp, true /* isWrappedStack */, 1.0, this.plotShaper.getCellMargin() ),
            cell, i, j, k, p, pi, col, row;

        info.calcStackSpacing( this.plotShaper.bins.maxCasesInCells );

        // for each cell
        for( i = 0; i < numCells; i++ ) {
            cell = plotCells[i];
            if( sortingAttribute ) {
                this.sortCasesInCell( cell, sortingAttribute, ioPositions, !sortingAttribute.isSortAscending );
            }

            // for cases in this cell
            for( j = 0, k = cell.cases.length; j < k; j++ ) {
                pi = cell.cases[j];
                Dyn.assertArrayIndex( ioPositions, pi );
                p = ioPositions[pi];
                col = j % info.numColumns;
                row = Dyn.mathUtil.floor( j / info.numColumns );

                // position in view coordinates
                if( isStackUp ) {
                    p.x = cell.centerX() - info.columnOffSet + col * info.columnWidth;
                    p.y = cell.bottom() - info.stackBottom - (row * info.stackSpacer);
                } else {
                    p.x = cell.left() + info.stackBottom + (row * info.stackSpacer);
                    p.y = cell.centerY() + info.columnOffSet - col * info.columnWidth;
                }
            }
        }
    };

    /**
     * Determine the stacking direction for dot plot and similar plots
     * @param xAxisView
     * @param yAxisView
     * @returns {boolean} true==stack bottom to top, false==stack left to right
     */
    calcStackDirection( xAxisView, yAxisView ) {
        var stackUp = !(  // default to stack up
            (yAxisView.isNumericActiveOrInactive() && !xAxisView.isNumericActiveOrInactive()) ||  // unless numeric axis on Y but not X
            (xAxisView.isEmptyOfAttributes() && !yAxisView.isEmptyOfAttributes()));	// or any attribute on Y but none on X;
        return stackUp;
    }

    displayOrderBy() {
        // true for dotplots but not for scatterplots
        // because we use Dyn.dataSet.getOrderByAttribute() for dot plot
        let Dyn = this.dyn;
        return this[Dyn.EPlotControl.kControlsCaseOrder];
    }

    allowCustomOrderBy() {
        // true for dotplots but not for scatterplots
        // because we use Dyn.dataSet.getOrderByAttribute() for dot plot
        let Dyn = this.dyn;
        return this[Dyn.EPlotControl.kControlsCaseOrder];
    }

    canUseY2Axis( subViewRef ) {
        let isNumericByNumericPlot = (subViewRef.xAxisView.type === 1) &&
            (subViewRef.yAxisView.type === 1);
        return isNumericByNumericPlot;
    }

    /** Change the icon style to circles, if needed, and reduce the circle size when lines are showing.  */
    calcIconStyle( ioPositions, xAxisView, yAxisView ) {
        let wantMultiIconPlot = Boolean( this.numAttributesUsedX > 1 || this.numAttributesUsedY > 1 );
        this.plotShaper.iconStyler.setStyleCircleOrCustomIcon( ioPositions.length, undefined, wantMultiIconPlot );
    }

} // class DotPlotBase
/**
 * StackHelper() -- Calculates common stack spacing parameters for stacking case icons.
 * Note: all numeric values are in view coordinates (number of pixels in the plot view).
 * @param iPlotCells {[Dyn.mathUtil.Rect]} -- Dyn.PlotShaper.bins.cells
 * @param iCaseIcon {Dyn.IconStyler} - reference to case icon to get size and type.
 * @param isStackUp {Boolean} true: stack from bottom to top; false: stack from left to right
 * @param isWrappedStack {Boolean} - if true we allow our stacks to wrap (multiple columns)
 * @param stackHeightFraction {number} - optional parameter [0-1] to limit stacking to a fraction of cell height.
 * @constructor
 */
class StackHelper {

    constructor( Dyn, iPlotCells, iCaseIcon, isStackUp, isWrappedStack, stackHeightFraction, cellMargin ) {
        this.dyn = Dyn;
        this.isStackUp = isStackUp;
        this.isWrappedStack = isWrappedStack;

        if( !stackHeightFraction ) stackHeightFraction = 1.0; // set optional parameter to default of 1

        // CAUTION:
        //		'height' means in the stacking direction (in Y if isStackUp, otherwise in X)
        //		'width' means in the perpendicular direction (in X if isStackUp, otherwise in Y)
        // we do this so that the calcStackSpacing works in either orientation
        this.iconHeight = iCaseIcon.getWidthOrHeight( !isStackUp );
        this.iconWidth = iCaseIcon.getWidthOrHeight( isStackUp ); // width (in X if isStackUp)

        // reserve extra space at top of cell for category count, percent, etc, if non-zero size.
        const extraMarginForCount = isStackUp ? cellMargin.y : cellMargin.x;
        //console.log( "Dyn.stackHelper extraMarginForCount:"+extraMarginForCount);

        // Dyn.PlotShaper cells are all the same size, and there are 1+ cells, so get size of 1st one.
        this.stackViewHeight = iPlotCells[0].widthOrHeight( !isStackUp );
        this.stackBottomMargin = Dyn.plotUtil.extraMarginForCaseOutlines + (0.5 * (this.stackViewHeight * (1 - stackHeightFraction)));
        this.stackTopMargin = Math.max( this.stackBottomMargin, Dyn.plotUtil.stackMargin + extraMarginForCount );
        this.stackViewHeight = this.stackViewHeight - (this.stackBottomMargin + this.stackTopMargin + this.iconHeight);
        this.stackViewWidth = iPlotCells[0].widthOrHeight( isStackUp ) - Dyn.plotUtil.stackMargin;

        this.stackBottom = Math.NaN;	// placement of first icon center at bottom of stack (in Y if isStackUp)
        this.stackSpacer = Math.NaN;	// space between each icon center (in Y if isStackUp)
        this.numColumns = Math.NaN;		// number of columns if isWrappedStack (in X if isStackUp)
        this.columnWidth = this.iconWidth;	// width of columns if isWrappedStack (in X if isStackUp)
        this.columnOffSet = 0;			// offset from cell center to place first column (in X if isStackUp)
    }

    /**
     * Calculate common stack spacing parameters.
     */
    calcStackSpacing( maxCases ) {
        var maxStackHeight,
            maxStackColumns,
            numIconsPerColumn,
            maxPixelsPerIcon,
            Dyn = this.dyn;

        if( !maxCases ) maxCases = 1;	// default to 1 if we don't know maxCases, to avoid restricting stack height

        // adjust stack width and height to number of cases and cell dimensions (assume all cells the same size)
        this.stackBottom = (this.iconHeight / 2) + this.stackBottomMargin;
        if( this.isWrappedStack ) {
            // calc stack columns assuming no overlap
            maxStackHeight = Math.floor( Math.max( 1, (this.stackViewHeight / this.iconHeight) ) );
            maxStackColumns = Math.max( 1, Dyn.mathUtil.floor( this.stackViewWidth / this.iconWidth ) );
            this.numColumns = Dyn.mathUtil.clipToMinMax( 1, maxStackColumns,
                Dyn.mathUtil.ceiling( maxCases / maxStackHeight ) );
            this.columnOffSet = (0.5 * ((this.numColumns - 1) * this.columnWidth));
            this.stackSpacer = this.iconHeight;
            // shrink space between stacks if needed to fit all icons with the given number of columns
            numIconsPerColumn = Dyn.mathUtil.floor( maxCases / this.numColumns );
            maxPixelsPerIcon = this.stackViewHeight / numIconsPerColumn;
            this.stackSpacer = Math.min( maxPixelsPerIcon, this.stackSpacer );
        } else {
            // adjust spacing for overlapping case icons if necessary for a single column stack
            this.numColumns = 1;
            this.stackSpacer = Math.max( 0, (this.stackViewHeight - this.iconHeight) / maxCases );
        }
        this.stackSpacer = Math.min( this.stackSpacer, this.iconHeight );
    }
} // class StackHelper

export class BubbleHelper {
    constructor( Dyn, plotElement ) {
        this.Dyn = Dyn;
        this.plotElement = plotElement;
    }
    getBubbleAttribute() {
        // for now, use the order by attribute as the bubble attribute.
        // We will want a separate bubble attribute if we allow users to sort by one attribute and size by another.
        return this.plotElement.plotShaper.dataSet.getBubbleSizeAttribute();
    }
    hasValidBubbleAttribute() {
        let Dyn = this.Dyn;
        return this.getBubbleAttribute()?.getDataType() === Dyn.dataUtil.types.kNumerical;
    }
    /**
     * Alows each element to respond to an axis button click
     * @param ioWhatChanged -- element should add a Dyn.EPlotChange parameter if returning true.
     * @param subViewRef
     * @param axisdata
     * @returns {boolean} -- true if handled by a Dyn.PlotElement
     */
    handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
        // handle plus and minus buttons for bubble size
        let Dyn = this.Dyn,
            iconStyler = this.plotElement.plotShaper.iconStyler,
            handled = false,
            isPlusButton  = (axisData.type==="plusButton"),
            isMinusButton = (axisData.type==="minusButton");

        if( ( isPlusButton || isMinusButton ) && this.hasValidBubbleAttribute() ) {
            let isIncrease = axisData.wantUndoOfClick ? ! isPlusButton : isPlusButton, // to handle undo
                caseSizeIndex = BubbleHelper.caseSizeIndex( iconStyler.getActualCaseIconSize() ),
                size = caseSizes.at( isIncrease ? caseSizeIndex + 1 : caseSizeIndex - 1 );
            iconStyler.setCustomCaseIconSize( size );
            ioWhatChanged[ Dyn.EPlotChange.kChAxisSize ] = true; // intervals affect axis range which affects axis view size.
            ioWhatChanged[ Dyn.EPlotChange.kChIconPosition ] = true; // icons must be positioned in new intervals
            handled = true;
            // // track analytics on histogram bin update
            // _gaq.push(['_trackEvent', 'Plot Function', 'Click', "Bins Updated"]);
        }
        return handled;
    }

    static caseSizeIndex = (s) => {
        let smallestDiff = caseSizes.at(-1),
            value = caseSizesDefault,
            index = 0;
        caseSizes.forEach( (n, i) => {
            let diff = Math.abs(n - s);
            if( diff < smallestDiff ) {
                smallestDiff = diff;
                index = i;
                value = n; // closest value
            }
        });
        return index;
    };

    legendPlotSpecificOptions( isIncompletePlot) {
        let Dyn = this.Dyn,
            iconStyler = this.plotElement.plotShaper.iconStyler,
            caseIconSize = iconStyler.getActualCaseIconSize();
        return {
            // for color legend
            disableAttribute: isIncompletePlot, // show color attribute as disabled if plot needs X or Y attribute
            isIncompleteAxisStyle: false, // don't require color attribute for bubble plot
            // for bubble size legend
            bubblePrompt: Dyn.loc( Dyn.plotStrings.promptValue ),
            showPlusMinus: this.hasValidBubbleAttribute(), // show plus and minus buttons for bubble size
            disablePlusButton: caseIconSize >= caseSizes.at(-1), // go no higher than max size
            disableMinusButton: caseIconSize <= caseSizes.at(0), // go no lower than min size
            plusButtonTip: Dyn.loc("increase bubble size"),
            minusButtonTip: Dyn.loc("decrease bubble size"),
        };
    }
}   // class BubbleHelper

export const wrapDynDotPlotElement = function(Dyn) {

    Dyn.DotPlotBase = DotPlotBase;

/**
 * Dyn.DotPlotElement()
 */
class DotOrScatterPlotElement extends DotPlotBase {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsShape,
            otherControls: []
        }, Dyn );

        this.wantStackUp = true; // else stack left-to-right
        this.needsXYPrompt = true;
        this.needsY2Prompt = true;
    }

    get kDefaultTitle() {
        return Dyn.plotTitles.DOT_PLOT;
    }

    get kScatterTitle() {
        return Dyn.plotTitles.SCATTER_PLOT;
    }

    get kUndoAction() {
        return Dyn.plotTitles.DOT_PLOT;
    }

    save() {
        let plotElementModel = super.save(); // call inherited save()
        // add any additional data here
        return plotElementModel;
    }

    /** Restore the plot, and restore the prompt axis if saved that way */
    restore( savedData, partialPlotState ) {
        super.restore( savedData ); // call inherited restore()
        // don't set axis prompts, axis will restore prompts if saved #1335
        this.needsXYPrompt = false;
        this.needsY2Prompt = false;
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef ) {
        var xAxisView = subViewRef.xAxisView, yAxisView = subViewRef.yAxisView, topMargin = subViewRef.topMargin;

        // use horizontal orientation except when the existing axes have numeric on Y but not X.
        let stackUp = this.calcStackDirection( xAxisView, yAxisView ),
            isScatterPlot = (xAxisView.isNumericActiveOrInactive() && yAxisView.isNumericActiveOrInactive());

        this.wantStackUp = stackUp;
        this[Dyn.EPlotControl.kControlsCaseOrder] = !isScatterPlot; // true; // sorting of cases only when not a scatterplot

        // update default plot once on plot creation, or when switching from dotplot to scatterplot.
        // being careful not to change the title if user-edited.
        const defaultDotplotTitle = Dyn.loc( this.kDefaultTitle ),
            defaultScatterTitle = Dyn.loc( this.kScatterTitle );
        this.plotShaper.setDefaultPlotTitle( topMargin, defaultDotplotTitle );
        let plotTitle = topMargin.getTitle();
        if( plotTitle === defaultDotplotTitle && isScatterPlot ) {
            this.plotShaper.setDefaultPlotTitle( topMargin, defaultScatterTitle, true );
        } else if( plotTitle === defaultScatterTitle && !isScatterPlot ) {
            this.plotShaper.setDefaultPlotTitle( topMargin, defaultDotplotTitle, true );
        }

        // update axis prompts once on dot plot creation
        if( this.needsXYPrompt ) {
            if( xAxisView.isEmptyOfAttributes() ) {
                xAxisView.setTitle( Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptX ) );
            }
            if( yAxisView.isEmptyOfAttributes() ) {
                yAxisView.setTitle( Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptY ) );
            }
            this.needsXYPrompt = false;
        }
        if( this.needsY2Prompt ) {
            if( subViewRef.y2AxisView && subViewRef.y2AxisView.isEmptyOfAttributes() ) {
                subViewRef.y2AxisView.setTitle( Dyn.loc( Dyn.plotStrings.promptY2 ) );
                this.needsY2Prompt = false;
            }
        }
        // or show X-axis prompt if both axes are empty of attributes (either dot plot or scatterplot or category plot OK)
        this.isIncompletePlot = Boolean( xAxisView.isEmptyOfAttributes() && yAxisView.isEmptyOfAttributes() );
        if( this.isIncompletePlot ) {
            xAxisView.setTitle( Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptX ) );
        }

        // update axis settings
        xAxisView.setPlotSpecificOptions( {
            numActiveAttributes: this.numAttributesUsedX,
            isIncompleteAxisStyle: this.isIncompletePlot  // if no attributes, show 'incomplete' X only, but accept an attribute on either X or Y
        } );
        yAxisView.setPlotSpecificOptions( { numActiveAttributes: this.numAttributesUsedY1 } );
        if( subViewRef.y2AxisView ) {
            subViewRef.y2AxisView.setPlotSpecificOptions( { numActiveAttributes: this.numAttributesUsedY2 } );
        }
        subViewRef.topMargin.setPlotSpecificOptions( null );
        subViewRef.rightMargin.setPlotSpecificOptions( { "disableAttribute": this.isIncompletePlot } );
    }

    isDefaultTitleForPlot( title ) {
        return Boolean( title === Dyn.loc( this.kDefaultTitle ) || title === Dyn.loc( this.kScatterTitle ) );
    }

} // class DotOrScatterPlotElement

    /**
     * Class for plots that expect a single attribute on the X and Y axes,
     * but can accept both Y and X attributes to look like a scatter plot.
     * and can accept multiple attributes on either Y or X, not both.
     */
    class DotPlotElement extends DotOrScatterPlotElement {
        constructor( Dyn ) {
            super( Dyn );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsDotPlot,
                otherControls: [
                    Dyn.EPlotControl.kControlsCaseOrder, // NOTE: overridden in .calcAxis()
                    Dyn.EPlotControl.kControlsY2Axis ]
            }, Dyn );
            this[Dyn.EPlotControl.kControlsDotPlot] = true; // is a dot plot
        }
    }
    Dyn.DotPlotElement = DotPlotElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsDotPlot, Dyn.DotPlotElement );

    /**
     * Class for plots that expect a single attribute on both X and Y axes,
     * and can accept multiple attributes on either Y or X, not both.
     */
    class ScatterPlotElement extends DotOrScatterPlotElement {
        constructor( Dyn ) {
            super( Dyn );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsScatterPlot,
                otherControls: [
                    Dyn.EPlotControl.kControlsCaseOrder, // NOTE: overridden in .calcAxis()
                    Dyn.EPlotControl.kControlsY2Axis ]
            }, Dyn );
            // this[Dyn.EPlotControl.kControlsDotPlot] = false; // not a dot plot
        }

        get kDefaultTitle() {
            return Dyn.plotTitles.SCATTER_PLOT;
        }

        /** Update the plot's axis model */
        calcAxis( ioWhatChanged, subViewRef ) {
            super.calcAxis( ioWhatChanged, subViewRef );

            // update default titles once on plot creation, then let user customize (don't change on recalc)
            // this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ) );

            // override Dotplot behavior to show prompt and incomplete axis style if X or Y axis is missing
            let xAxisView = subViewRef.xAxisView,
                yAxisView = subViewRef.yAxisView,
                isScatterPlot = (xAxisView.isNumericActiveOrInactive() && yAxisView.isNumericActiveOrInactive());
            this.isIncompletePlot = !isScatterPlot;
            let wantIncompleteStyleOnX = Boolean( this.isIncompletePlot && !xAxisView.isNumericActiveOrInactive() ),
                wantIncompleteStyleOnY = Boolean( this.isIncompletePlot && !yAxisView.isNumericActiveOrInactive() );
            if( xAxisView.isEmptyOfAttributes() ) {
                xAxisView.setTitle( Dyn.loc( Dyn.plotStrings.promptValue ) );
            }
            if( yAxisView.isEmptyOfAttributes() ) {
                yAxisView.setTitle( Dyn.loc( Dyn.plotStrings.promptValue ) );
            }

            // clear axis settings from other shapers, show multiple attributes in use (else they will be dimmed)
            xAxisView.setPlotSpecificOptions( {
                numActiveAttributes: this.numAttributesUsedX,
                isIncompleteAxisStyle: wantIncompleteStyleOnX
            } );
            yAxisView.setPlotSpecificOptions( {
                numActiveAttributes: this.numAttributesUsedY1,
                isIncompleteAxisStyle: wantIncompleteStyleOnY
            } );
        }
    }
    Dyn.ScatterPlotElement = ScatterPlotElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsScatterPlot, Dyn.ScatterPlotElement );


    /** class for bubble charts: just like a Dotplot or Scatterplot, but also accepts a Bubble attribute */
    class BubbleChartElement extends DotPlotElement {

        constructor( Dyn ) {
            super( Dyn );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityShape,
                replaceType: Dyn.EPlotControl.kControlsShape,
                primaryType: Dyn.EPlotControl.kControlsBubbleChart,
                otherControls: [
                    Dyn.EPlotControl.kControlsCaseOrder, // NOTE: overridden in .calcAxis()
                    Dyn.EPlotControl.kControlsY2Axis,
                    Dyn.EPlotControl.kControlsBubbles ]
            }, Dyn );
            this[Dyn.EPlotControl.kControlsDotPlot] = false; // not a dot plot

            this.wantStackUp = true; // else stack left-to-right
            this.needsXYPrompt = true;
            this.needsY2Prompt = true;
            this.bubbleHelper = new BubbleHelper( Dyn, this );
        }

        get kDefaultTitle() {
            return Dyn.plotTitles.BUBBLE_CHART;
        }

        /** Change the icon style to circles, if needed, and reduce the circle size when lines are showing.  */
        calcIconStyle( ioPositions, xAxisView, yAxisView ) {
            let wantMultiIconPlot = Boolean( this.numAttributesUsedX > 1 || this.numAttributesUsedY > 1 );
            this.plotShaper.iconStyler.setStyleCircleOrCustomIcon( ioPositions.length, Dyn.plotUtil.bubblePlotSizeFraction, wantMultiIconPlot );
        }


        calcAxis( ioWhatChanged, subViewRef ) {
            // DotPlotElement.calcAxis() will set the default title and axis prompts
            // and set this.isIncompletePlot if both axes are empty of attributes
            super.calcAxis( ioWhatChanged, subViewRef );

            this[Dyn.EPlotControl.kControlsCaseOrder] = true; // always allow sorting of cases, even if a scatterplot-like bubble chart

            let legendPlotSpecificOptions = this.bubbleHelper.legendPlotSpecificOptions( this.isIncompletePlot );
            subViewRef.rightMargin.setPlotSpecificOptions( legendPlotSpecificOptions );
        }

        getBubbleAttribute() {
            return this.bubbleHelper.getBubbleAttribute();
        }

        hasValidBubbleAttribute() {
            return this.bubbleHelper.hasValidBubbleAttribute();
        }

        handleAxisButtonClick( ioWhatChanged, subViewRef, axisData ) {
            return this.bubbleHelper.handleAxisButtonClick( ioWhatChanged, subViewRef, axisData );
        }
    } // class BubbleChartElement
    Dyn.BubbleChartElement = BubbleChartElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsBubbleChart, Dyn.BubbleChartElement );


/**
 * Dyn.LineGraphElement()
 *
 * Graph with dot for each case, and line connecting the cases.
 * (plus separate line for each cell, separate line for each of multiple attributes on one axis)
 */
class LineGraphElement extends DotPlotBase {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsLineGraph,
            otherControls: [
                Dyn.EPlotControl.kControlsCaseOrder,
                Dyn.EPlotControl.kControlsY2Axis ]
        }, Dyn );

        this.oneLinePerCategory = false; // optional parameter: one line per category if categories in legend
        //this.needsDefaultTitle = true;
        this.needsY2Prompt = true;
        this.gapValues = []; // data values of the order-by attribute that create gaps; see isMissingValueGapSegment()
    }

    get kDefaultTitle() {
        return Dyn.plotTitles.LINE_GRAPH;
    }

    get kUndoAction() {
        return Dyn.plotTitles.LINE_GRAPH;
    }

    save() {
        let plotElementModel = super.save(); // call inherited save()
        // add any additional data here
        return plotElementModel;
    }

    /** Restore the plot, and restore the prompt axis if saved that way */
    restore( savedData, partialPlotState ) {
        super.restore( savedData ); // call inherited restore()
        // don't set axis prompts, axis will restore them if saved that way #1335
        this.needsY2Prompt = false;
    }

    calcNumIconsPerCase( ioWhatChanged, subViewRef ) {
        // get settings for a dot or scatterplot or category/category plot
        let iconsPerCase = this._calcNumIconsPerCaseWithX_Y1_Y2_Axes( ioWhatChanged, subViewRef );

        // but disallow categorical attributes on the X or Y axis
        // Note: later call to calcAxis() will use these settings to enable/disable attribute on axis
        if( subViewRef.xAxisView.isCategoricalActiveOrInactive() ) {
            this.numAttributesUsedX = 0;
        }
        if( subViewRef.yAxisView.isCategoricalActiveOrInactive() ) {
            this.numAttributesUsedY1 = 0;
            this.numAttributesUsedY = this.numAttributesUsedY1 + this.numAttributesUsedY2;
        }
        if( subViewRef.y2AxisView && subViewRef.y2AxisView.isCategoricalActiveOrInactive() ) {
            this.numAttributesUsedY2 = 0;
            this.numAttributesUsedY = this.numAttributesUsedY1 + this.numAttributesUsedY2;
        }
        // max of number of icons used on any one axis, and no less than 1.
        iconsPerCase = Math.max( 1, Math.max( this.numAttributesUsedX, this.numAttributesUsedY ) );

        return iconsPerCase;
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef ) {
        var xAxisView = subViewRef.xAxisView, yAxisView = subViewRef.yAxisView, topMargin = subViewRef.topMargin;

        // update default titles once on plot creation, then let user customize (don't change on recalc)
        this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ) );

        // show prompt and incomplete axis style if X, Y, or Y2 axis is missing
        let isScatterPlot = (xAxisView.isNumericActiveOrInactive() && yAxisView.isNumericActiveOrInactive());
        this.isIncompletePlot = !isScatterPlot;
        let wantIncompleteStyleOnX = Boolean( this.isIncompletePlot && !xAxisView.isNumericActiveOrInactive() ),
            wantIncompleteStyleOnY = Boolean( this.isIncompletePlot && !yAxisView.isNumericActiveOrInactive() ),
            wantIncompleteStyleOnLegend = (this.oneLinePerCategory && !subViewRef.rightMargin.getAttribute()),
            wantDisabledLegend = this.isIncompletePlot;
        if( xAxisView.isEmptyOfAttributes() ) {
            xAxisView.setTitle( Dyn.loc( Dyn.plotStrings.promptValue ) );
        }
        if( yAxisView.isEmptyOfAttributes() ) {
            yAxisView.setTitle( Dyn.loc( Dyn.plotStrings.promptValue ) );
        }
        if( this.needsY2Prompt ) {
            if( subViewRef.y2AxisView && subViewRef.y2AxisView.isEmptyOfAttributes() ) {
                subViewRef.y2AxisView.setTitle( Dyn.loc( Dyn.plotStrings.promptY2 ) );
                this.needsY2Prompt = false;
            }
        }

        // clear axis settings from other shapers, show multiple attributes in use (else they will be dimmed)
        xAxisView.setPlotSpecificOptions( {
            numActiveAttributes: this.numAttributesUsedX,
            isIncompleteAxisStyle: wantIncompleteStyleOnX
        } );
        yAxisView.setPlotSpecificOptions( {
            numActiveAttributes: this.numAttributesUsedY1,
            isIncompleteAxisStyle: wantIncompleteStyleOnY
        } );
        if( subViewRef.y2AxisView ) {
            subViewRef.y2AxisView.setPlotSpecificOptions( { 'numActiveAttributes': this.numAttributesUsedY2 } );
        }
        subViewRef.topMargin.setPlotSpecificOptions( null );
        subViewRef.rightMargin.setPlotSpecificOptions( {
            disableAttribute: wantDisabledLegend,
            isIncompleteAxisStyle: wantIncompleteStyleOnLegend
        } );

        let colorAttribute = subViewRef.rightMargin.getColorAttribute();
        this.canSonifyByColorAttribute = isScatterPlot && colorAttribute && colorAttribute.hasNoteStringData();
        this[Dyn.EPlotControl.kControlsCaseOrder] = true; // for .displayOrderBy(), for override of X-axis sorting
        this.wantStackUp = this.calcStackDirection( xAxisView, yAxisView ); // for when 0 or 1 attribute present
    }

    /** Change the icon style to circles, if needed, and reduce the circle size when lines are showing.  */
    calcIconStyle( ioPositions, xAxisView, yAxisView ) {
        let wantMultiIconPlot = Boolean( this.numAttributesUsedX > 1 || this.numAttributesUsedY > 1 );
        this.plotShaper.iconStyler.setStyleCircleOrCustomIcon( ioPositions.length, Dyn.plotUtil.circleWithLinesSizeFraction, wantMultiIconPlot );
    }

    /**
     * Record which cases that have missing values for the given plot.
     */
    calcMissingValueCases( numCases, xAxisView, yAxisView ) {
        var missingCases = this.plotShaper.dataSet.missingCases,
            orderByAttribute = this.getOrderByAttributeForLines( this.plotShaper.dataSet, xAxisView, yAxisView );
        const usePreFilterStats = true; // so that filtered-out categories are not marked as missing (they should be marked as filtered only)

        xAxisView.postCalcAxisUpdate(); // update axis type if changed during calcAxis()
        yAxisView.postCalcAxisUpdate();

        // reset list of gap values; we will re-create it below
        this.gapValues = [];

        // for each case
        for( let caseIndex = 0; caseIndex < numCases; caseIndex++ ) {

            // for each attribute used in x and y
            let isValid = true;
            for( let attrIndex = 0; isValid && (attrIndex < this.numAttributesUsedX); attrIndex++ ) {
                if( !attributeValueIsValid( caseIndex, attrIndex, xAxisView ) ) {
                    isValid = false;
                    saveGapValue( this.gapValues, orderByAttribute, caseIndex );
                }
            }
            for( let attrIndex = 0; isValid && (attrIndex < this.numAttributesUsedY); attrIndex++ ) {
                if( !attributeValueIsValid( caseIndex, attrIndex, yAxisView ) ) { // Note: includes Dual Axis (y2 axis)
                    isValid = false;
                    saveGapValue( this.gapValues, orderByAttribute, caseIndex );
                }
            }
            missingCases.setCaseIsMissing( caseIndex, !isValid );
        }

        // sort gap values from lowest to highest for search efficiency)
        this.gapValues.sort( ( a, b ) => (a - b) );
        //console.log( "gap values", this.gapValues );

        // exclude case if any attribute has a missing value (e.g. blank categorical value, non-numeric on numeric axis)
        function attributeValueIsValid( caseIndex, attributeIndex, axisView ) {
            var value = axisView.getCaseDataAsNumber( caseIndex, attributeIndex, usePreFilterStats );
            return isFinite( value );
        }

        // save as a "gap value" if the order-by attribute value of this case index is finite numeric
        // If we have missing values between non-missing values in the sorted cases,
        // then we have a "gap" section of the line that must be marked with a dash.
        function saveGapValue( gapValueArray, orderByAttribute, caseIndex ) {
            if( orderByAttribute ) {
                let value = orderByAttribute.cases[caseIndex];
                if( Dyn.dataUtil.getDataType( value ) === "number" ) {
                    gapValueArray.push( value );
                }
            }
        }
    }

    /**
     * Update the adornments array for the line graph.
     */
    calcAdornments( ioAdornArray, casePositions, xAxisView, yAxisView, noAnimation ) {
        "use strict";
        var thisLineGraph = this,
            plotCells = this.plotShaper.bins.cells,
            dataSet = this.plotShaper.dataSet,
            legendAttribute = dataSet.getSelectedAttribute(),
            numCells = plotCells.length,
            useXAttrs = Math.max( this.numAttributesUsedX > this.numAttributesUsedY ),
            multiAttributeArray = (useXAttrs ? xAxisView : yAxisView).allAttributes.getAttributesArray(),
            numAttributesOnAxis = Math.max( 1, (useXAttrs ? this.numAttributesUsedX : this.numAttributesUsedY) ),
            numCategoriesInLegend = this.oneLinePerCategory ? getNumCategoriesInLegend( legendAttribute ) : 1,
            numLinesPerCell = numAttributesOnAxis * numCategoriesInLegend,
            newNumLines = numCells * numLinesPerCell,
            sortingAttribute = this.getOrderByAttributeForLines( dataSet, xAxisView, yAxisView ),
            clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
            animationTime = (noAnimation ? Dyn.plotUtil.noAnimationMs : Dyn.plotUtil.stdAnimationTimeMs),
            pathInfo = [],
            gapSegments = [],
            i, j, p, px, py, cell, caseCount, attrIndex, lineIndex, categoryIndex, elem;

        // Update the order by attribute if lineOrder and datasetOrder attribute
        // is not same
        if( sortingAttribute !== dataSet.getOrderByAttribute() ) {
            // set the order by attribute, so that appropriate info appear on plot
            dataSet.setOrderByAttribute( {
                id: sortingAttribute.id,
                noUndo: true // no extra undo step; PlotView.undo() of "addElement" will handle
            } );
        }
        // for each cell, add or modify a path string
        //      Number of lines will be:
        //          1 per cell, times
        //          1 per attribute (if multiple attributes on any one axis)
        //          1 per category (if multiple categories in legend attribute and oneLinePerCategory==true)
        for( i = 0; i < numCells; i++ ) {
            cell = plotCells[i];
            caseCount = cell.cases.length;
            if( sortingAttribute ) {
                this.sortCasesInCell( cell, sortingAttribute, casePositions, !sortingAttribute.isSortAscending );
            }

            // create new path string for each attribute/category combination in cell
            pathInfo = [];
            gapSegments = []; // erase gap segments from previous cell
            for( j = 0; j < numLinesPerCell; j++ ) {
                pathInfo[j] = {
                    lineIndex: (i * numLinesPerCell) + j, // skip over lines from previous cells
                    attrIndex: Math.floor( j / numCategoriesInLegend ), // first attribute first
                    categoryIndex: j % numCategoriesInLegend, // then iterate through categories, if any
                    segments: [],
                    orderByValues: [],
                    //pathString: ''
                };
            }

            // add points to each path string (two points make a line segment)
            for( j = 0; j < caseCount; j++ ) {
                Dyn.assertArrayIndex( casePositions, cell.cases[j] );
                p = casePositions[cell.cases[j]];
                if( p.isVisible ) {
                    // create the line path string; move to 1st point, line to 2nd+ point.
                    attrIndex = (useXAttrs ? p.xAttrIndex : p.yAttrIndex);
                    categoryIndex = getCategoryIndex( p.caseIndex, numCategoriesInLegend, legendAttribute );
                    if( categoryIndex >= 0 ) { // if case not excluded from categories because of missing value (only happens with this.oneLinePerCategory===true)
                        lineIndex = (attrIndex * numCategoriesInLegend) + categoryIndex;
                        px = p.x.toFixed( 1 );
                        py = p.y.toFixed( 1 );
                        //pathStrings[lineIndex] += (pathStrings[lineIndex] ? 'L' : 'M' ) + px + ',' + py;
                        pathInfo[lineIndex].orderByValues.push( sortingAttribute ? sortingAttribute.cases[p.caseIndex] : Number.NaN );
                        pathInfo[lineIndex].segments.push( px + ',' + py );

                        // create gap segments if needed (see isMissingValueGapSegment())
                        if( this.gapValues.length && sortingAttribute ) { // not possible if no gap values or sorting attribute
                            let orderByArray = pathInfo[lineIndex].orderByValues,
                                arrayLength = orderByArray.length;
                            if( arrayLength >= 2 &&
                                this.isMissingValueGapSegment( orderByArray[arrayLength - 2], orderByArray[arrayLength - 1] ) ) {
                                let lineSegments = pathInfo[lineIndex].segments;
                                gapSegments.push( {
                                    lineIndex: lineIndex,
                                    categoryIndex: categoryIndex,
                                    attrIndex: attrIndex,
                                    segments: [ lineSegments[arrayLength - 2], lineSegments[arrayLength - 1] ],
                                    isGapSegment: true
                                } );
                            }
                        }
                    }
                }
            }
            // add gap segments to list of lines
            gapSegments.forEach( ( element ) => {
                pathInfo.push( element );
                element.lineIndex = newNumLines;
                ++newNumLines;
            } );
            // construct SVG path for each line
            pathInfo.forEach( ( element, index ) => {
                if( element.segments.length <= 0 ) {
                    element.pathString = this.plotShaper.iconStyler.calcEmptyPath()
                } else if( element.segments.length === 1 ) {
                    element.pathString = 'M' + element.segments[0];
                } else {
                    element.pathString = element.segments.reduce( ( prev, curr, index ) => (index > 1) ? prev + 'L' + curr : 'M' + prev + 'L' + curr );
                }
            }, this );

            // add/remove/move line adornments, to match path strings we just created.
            pathInfo.forEach( ( element, index ) => {
                const gapStrokeWidth = 3,
                    gapStrokeColor = "#eee",
                    gapDashArray = '5,1';
                lineIndex = element.lineIndex;
                attrIndex = element.attrIndex;
                categoryIndex = element.categoryIndex;

                // add the line if it does not exist yet
                if( !ioAdornArray[lineIndex] ) {
                    ioAdornArray.push( this.makeStartingPathForCell( cell.cases, casePositions, j ) );
                    animationTime = Dyn.plotUtil.noAnimationMs; // animation is only for moving existing lines
                    Dyn.assert( ioAdornArray.length === (lineIndex + 1), "expecting one+ line per cell" );
                }
                // animate to the new line position, and if using a starting path, increase opacity from 0 to 100%
                elem = ioAdornArray[lineIndex];
                elem.attr( 'clip-rect', clipRectString )
                    .animate( {
                        'path': element.pathString,
                        //'stroke-dasharray':element.isGapSegment ? "--..":". ", // does not work, see setAttribute below
                        'stroke-width': element.isGapSegment ? gapStrokeWidth : Dyn.plotUtil.lineGraphWidth,
                        'opacity': 1,
                        'stroke': element.isGapSegment ?
                            gapStrokeColor :
                            getLineStrokeColor( attrIndex, categoryIndex, numAttributesOnAxis, multiAttributeArray, numCategoriesInLegend, legendAttribute )
                    }, animationTime, Dyn.plotUtil.stdAnimationEasing )
                    .data( "tooltip", getToolTipString( attrIndex, categoryIndex, numAttributesOnAxis, multiAttributeArray, numCategoriesInLegend, legendAttribute, element.isGapSegment ) );

                // Note: attr('stroke-dasharray','- ') is supposed to work but does not, others report this also.
                // See: http://dandouglas.net/raphael-js-stroke-dasharray-problem-solved/
                elem.node.setAttribute( 'stroke-dasharray', element.isGapSegment ? gapDashArray : null );

                // only if tooltip is available
                if( elem.data( "tooltip" ) ) {
                    elem.hover( overScope, outScope ); // hover only if multiple attribute and/or multiple category lines in a cell
                } else {
                    elem.unhover();
                }

            }, this );
        }

        // remove any extra lines from previous plot
        if( ioAdornArray.length > newNumLines ) {
            for( i = ioAdornArray.length - 1; i >= newNumLines; i -= 1 ) {
                ioAdornArray[i].remove();
            }
            ioAdornArray.length = newNumLines;
        }

        function getNumCategoriesInLegend( legendAttribute ) {
            return (legendAttribute && legendAttribute.stats.isCategorical() && legendAttribute.stats.categories.length > 1) ? legendAttribute.stats.categories.length : 1;
        }

        function getCategoryIndex( caseIndex, numCategoriesInLegend, legendAttribute ) {
            return ((numCategoriesInLegend > 1) ?
                // NOTE: category index can be -1 if multiple categories, yet case has missing value for legend attribute.
                // Caller should then exclude this point from any of the lines
                legendAttribute.stats.categories.indexOf( legendAttribute.cases[caseIndex] ) :
                0); // don't use categories
        }

        function getLineStrokeColor( attribIndex, categoryIndex, numAttributesOnAxis, attributeArray, numCategoriesInLegend, legendAttribute ) {
            if( thisLineGraph.isIncompletePlot ) {
                return Dyn.plotUtil.kCaseMissingValueColor;
            } else if( thisLineGraph.oneLinePerCategory && (numCategoriesInLegend > 1) ) {
                // if multiple categories (and also possibly multi-attribute), different line color for each category
                Dyn.assertArrayIndex( legendAttribute.stats.categories, categoryIndex, "invalid category index" );
                return Dyn.colorUtil.calcCategoryColor(
                    legendAttribute.statsPreFilter,
                    legendAttribute.color,
                    legendAttribute.stats.categories[categoryIndex],
                    false,
                    legendAttribute.colorMap,
                    legendAttribute.isSortAscending ).colorString;
            } else if( numAttributesOnAxis > 1 ) {
                // different line color for each attribute in multi-attribute plot (only if not one-line-per-category)
                Dyn.assertArrayIndex( attributeArray, attribIndex, "invalid multi-attribute count" );
                return attributeArray[attribIndex].multiAttrStrokeColor.colorString;
            } else {
                return Dyn.plotUtil.lineGraphStroke;
            }
        }

        function getToolTipString( attribIndex, categoryIndex, numAttributesOnAxis, attributeArray, numCategoriesInLegend, legendAttribute, isMissingLine ) {

            if( isMissingLine ) {
                return "missing values";
            }

            // get bare numeric values as strings
            var tip = '';
            if( numAttributesOnAxis > 1 ) {
                Dyn.assertArrayIndex( attributeArray, attribIndex, "invalid multi-attribute count" );
                tip = attributeArray[attribIndex].name;
            }
            if( (numAttributesOnAxis > 1) && (numCategoriesInLegend > 1) ) {
                tip += ", ";
            }
            if( numCategoriesInLegend > 1 ) {
                Dyn.assertArrayIndex( legendAttribute.stats.categories, categoryIndex, "invalid category index" );
                tip += legendAttribute.axisValueToString( legendAttribute.stats.categories[categoryIndex] );
            }
            // insert into translatable text
            return tip;
        }

        function overScope( event ) {
            thisLineGraph.addGlow( this, this.attr( 'stroke' ) );
            Dyn.toolTip.show( this, event, { x: this.attrs.x, y: this.attrs.y } );
        }

        function outScope( event ) {
            if( Dyn.toolTip.isMyElement( this ) )
                Dyn.toolTip.hide();
            thisLineGraph.removeGlow();
        }
    }


    /**
     * Return a starting path for a single line in the LineGraph line,
     * that is a good starting position to animate from
     *
     * @param casesInCell {[]} - array of indicies of cases in this cell/line
     * @param casePositions {*} - x, y, isVisible final position and status of case in plot.
     * @param attributeIndex {Number} - if multiple icons per case, which attribute index do we match?
     * @returns {*} - path string for Rafael.paper.path()
     */
    makeStartingPathForCell( casesInCell, casePositions, attributeIndex ) {
        "use strict";
        var plotViewPaper = this.plotShaper.adornments.paper,
            iconStyler = this.plotShaper.iconStyler,
            px = Math.round( iconStyler.missingValuePosition.x ).toFixed(),
            py = Math.round( iconStyler.missingValuePosition.y ).toFixed(),
            useXAttrs = Math.max( this.numAttributesUsedX > this.numAttributesUsedY ),
            i, j = casesInCell.length, p,
            pathString = '', pathElement;

        for( i = 0; i < j; i++ ) {
            p = casePositions[casesInCell[i]];
            if( p.isVisible && (useXAttrs ? p.xAttrIndex === attributeIndex : p.yAttrIndex === attributeIndex) ) {
                pathString += (pathString ? 'L' : 'M') + px + ',' + py;
            }
        }
        if( !pathString ) {
            // make an empty path if no cases in cell
            pathString = iconStyler.calcEmptyPath();
        }
        pathElement = plotViewPaper.path( pathString );
        pathElement.attr( {
            'stroke-width': Dyn.plotUtil.lineGraphWidth,
            'stroke': Dyn.plotUtil.lineGraphStroke,
            'opacity': 0
        } );
        return pathElement;
    }

    /**
     * Get the Attribute used to order lines, if any. No Attribute === default to case order.
     * @param dataSet
     * @param xAxisView
     * @param yAxisView
     * @returns {*|Dyn.Attribute|null} -- return Dyn.Attribute or null, used to connect lines
     */
    getOrderByAttributeForLines( dataSet, xAxisView, yAxisView ) {
        // if no user sort, default to X-axis which is correct for typical time-series plot,
        // if no user sort attribute or X-axis, default to Y-axis attribute, then no sorting (case order)
        // NOTE: this means that user picking Order by: Case in the menu, will actually get X or Y axis order.
        // we would need to add more options in the menu to fix this (e.g. "Case order" versus "No order" versus "X-axis Order"?)
        var sortingAttribute = dataSet.getOrderByAttribute();
        if( !sortingAttribute ) {
            sortingAttribute = (xAxisView.type === 1 ? xAxisView.getAttribute() : (yAxisView.type === 1 ? yAxisView.getAttribute() : null));
        }
        return sortingAttribute;
    }

    /**
     * Is the line segment in a gap created by missing values?
     * The line segment is defined by the two finite values of the order-by attribute.
     * @param lowerValue
     * @param upperValue
     * @returns {boolean}
     */
    isMissingValueGapSegment( lowerValue, upperValue ) {
        let isInGap = false;
        Dyn.assert( isFinite( lowerValue ) && isFinite( upperValue ) && (upperValue >= lowerValue), "lowerValue should be < upperValue: ", lowerValue, " ", upperValue );
        if( this.gapValues.length > 0 ) {
            let gapIndex = this.gapValues.findIndex( v => (v > lowerValue) && (v < upperValue) );
            // this is a gap segment if there is at least one gap value between the lower and upper values
            if( gapIndex >= 0 ) Dyn.debugging && console.log( "isMissingValueGapSegment ", this.gapValues[gapIndex] );
            return (gapIndex >= 0);
        }
        return false;
    }

    /**
     * Get plot sonification data for the Line Graph
     * @returns {{caseIndices: [], numNotes: number, type: string, noteIndex: number}}
     */
    getPlotSonificationData() {
        let soundData = {
            type: 'caseValues',
            axis: this.canSonifyByColorAttribute ? 'C' : 'Y',
            iconIndices: [],
            notes: [],
        };
        let plotCells = this.plotShaper.bins.cells,
            plotView = Dyn.plotView;
        // get non-missing case indices in line graph order, as sorted by calcAdornments()
        plotCells.forEach( ( cell ) => {
            cell.cases.forEach( ( iconIndex ) => { // for each icon position index
                let noteData = plotView.getIconAxisSonficationData( iconIndex, soundData.axis );
                soundData.iconIndices.push( iconIndex );
                soundData.notes.push( noteData );
            } );
        } );
        return soundData;
    }
} // class LineGraphElement

Dyn.LineGraphElement = LineGraphElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsLineGraph, Dyn.LineGraphElement );

/**
 * Dyn.LineGraphSingleElement()
 */
class LineGraphSingleElement extends LineGraphElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsLineGraphSingle,
            otherControls: [
                Dyn.EPlotControl.kControlsCaseOrder ]
        }, Dyn );

        this.oneLinePerCategory = false; // optional parameter: one line per category if categories in legend
        //this.needsDefaultTitle = true;
    }

    get kDefaultTitle() { return Dyn.plotTitles.LINE_GRAPH; }
    get kUndoAction()   { return Dyn.plotTitles.LINE_GRAPH; }

    replaces( iExistingPlotElement, ioPlotAdornments )
    {
        // extend replaces() to preserve old adornments before they are deleted by PlotElement.destroy()
        if( iExistingPlotElement.primaryType === Dyn.EPlotControl.kControlsLineAndCategoryGraph ) {
            // move lines from the other Line Graph type, instead of deleting them, so calcAdornments() can animate them
            ioPlotAdornments.renameAdornmentsArray( iExistingPlotElement.primaryType, this.primaryType );
        }
        return super.replaces( iExistingPlotElement );
    }
} // class LineGraphSingleElement

Dyn.LineGraphSingleElement = LineGraphSingleElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsLineGraphSingle, Dyn.LineGraphSingleElement );

/**
 * Dyn.LineAndCategoryElement()
 *
 * Graph with dot for each case, and line connecting the cases.
 *  Has a separate line for each category in the legend attribute
 *  (plus separate line for each cell, separate line for each of multiple attributes on one axis)
 */
class LineAndCategoryElement extends LineGraphElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsLineAndCategoryGraph,
            otherControls: [
                Dyn.EPlotControl.kControlsCaseOrder ]
        }, Dyn );

        this.oneLinePerCategory = true; // optional parameter: one line per category if categories in legend
        //this.needsDefaultTitle = true;
    }

    get kDefaultTitle() {
        return Dyn.plotTitles.LINE_GRAPH_WITH_CATEGORIES;
    }

    get kUndoAction() {
        return Dyn.plotTitles.LINE_GRAPH_WITH_CATEGORIES;
    }

    replaces( iExistingPlotElement, ioPlotAdornments ) {
        // extend replaces() to preserve old adornments before they are deleted by PlotElement.destroy()
        if( iExistingPlotElement.primaryType === Dyn.EPlotControl.kControlsLineGraphSingle ) {
            // move lines from the other Line Graph type, instead of deleting them, so calcAdornments() can animate them
            ioPlotAdornments.renameAdornmentsArray( iExistingPlotElement.primaryType, this.primaryType );
        }
        return super.replaces( iExistingPlotElement );
    }
} // class LineAndCategoryElement

Dyn.LineAndCategoryElement = LineAndCategoryElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsLineAndCategoryGraph, Dyn.LineAndCategoryElement );

} // wrapDynDotPlotElement
